/**
 * useAutoRehydration - Automatically rebuilds missing foundation data and processes configurations
 *
 * Handles two scenarios:
 * 1. Foundation data missing after page refresh (rebuilds dateRecords, taxableValueMap)
 * 2. Selected configuration loaded from API without candidates (auto-processes on load)
 *
 * LAZY LOADING: Only processes the selected configuration on initial load.
 * Other configurations are processed on-demand via processConfigurationOnDemand().
 * Configurations created during the session are handled by useConfigurationWorkflow.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type ProjectData, useProjectStore } from '~/hooks/useProjectStore';
import { BuildService } from '~/services/build-service';
import { processConfigurationCandidates } from '~/services/configuration-processing';
import type { Configuration } from '~/types';
import { convertProjectDataToProject } from '~/utils/project-conversion';

interface UseAutoRehydrationProps {
  projectId: string | undefined;
  project: ProjectData | undefined;
  configurations: Configuration[] | null;
  selectedConfigurationId: string | null;
  onCandidateSelected: (candidateId: string) => void;
}

interface UseAutoRehydrationReturn {
  /** Process a specific configuration on-demand (for lazy loading) */
  processConfigurationOnDemand: (configId: string) => void;
  /** Check if a configuration needs processing */
  configNeedsProcessing: (configId: string) => boolean;
}

export const useAutoRehydration = ({
  projectId,
  project,
  configurations,
  selectedConfigurationId,
  onCandidateSelected,
}: UseAutoRehydrationProps): UseAutoRehydrationReturn => {
  // Track which configurations have been processed (for lazy loading)
  const processedConfigIdsRef = useRef<Set<string>>(new Set());

  // Convert once, memoized - avoids repeated conversion in processConfiguration
  const engineProject = useMemo(
    () => (project ? convertProjectDataToProject(project, true) : null),
    [project]
  );

  // Rebuild foundation data if missing (e.g., after page refresh or direct navigation)
  useEffect(() => {
    if (!projectId || !project) return;

    const hasDateInputs = !!project.dateInputs;
    const hasTaxInputs = !!project.taxValueInputs;
    const hasDebtInputs = !!project.existingDebtInputs && project.existingDebtInputs.length > 0;
    const missingDateRecords = !project.dateRecords || project.dateRecords.length === 0;
    const missingTaxMap = !project.taxableValueMap;
    const missingDebtProjects =
      !project.existingDebtProjects || project.existingDebtProjects.length === 0;

    // Rebuild dateRecords if we have inputs but missing records
    if (hasDateInputs && missingDateRecords) {
      BuildService.buildDateRecords(projectId);
    }

    // Rebuild taxableValueMap if we have inputs but missing map (requires dateRecords)
    if (hasTaxInputs && missingTaxMap && !missingDateRecords) {
      BuildService.buildTaxableValueMap(projectId);
    }

    // Rebuild existingDebtProjects if we have inputs but missing projects (requires dateRecords)
    if (hasDebtInputs && missingDebtProjects && !missingDateRecords) {
      BuildService.buildExistingDebtProjects(projectId);
    }
  }, [projectId, project]);

  // Helper: Check if a configuration needs processing
  const configNeedsProcessing = useCallback(
    (configId: string): boolean => {
      if (!configurations) return false;
      const config = configurations.find((c) => c.id === configId);
      if (!config) return false;

      // Already processed in this session
      if (processedConfigIdsRef.current.has(configId)) return false;

      // Has bonds but no candidates
      return (
        config.bondSeries &&
        config.bondSeries.length > 0 &&
        (!config.candidates || config.candidates.length === 0)
      );
    },
    [configurations]
  );

  // Helper: Process a single configuration
  const processConfiguration = useCallback(
    (config: Configuration, selectFirstCandidate: boolean) => {
      if (!projectId || !project || !engineProject) return;

      // Ensure foundation data exists
      const hasDateRecords = project.dateRecords && project.dateRecords.length > 0;
      const hasTaxMap = project.taxableValueMap && Object.keys(project.taxableValueMap).length > 0;
      if (!hasDateRecords || !hasTaxMap) return;

      const result = processConfigurationCandidates(engineProject, config);

      if (result.success) {
        useProjectStore.getState().updateConfiguration(projectId, result.configuration);
        processedConfigIdsRef.current.add(config.id);

        // Auto-select first candidate if requested
        const { candidates } = result.configuration;
        if (selectFirstCandidate && candidates && candidates.length > 0) {
          onCandidateSelected(candidates[0].id);
        }
      } else {
        console.error(`Error processing configuration ${config.id}:`, result.error);
      }
    },
    [projectId, project, engineProject, onCandidateSelected]
  );

  // Process a configuration on-demand (called when user selects an unprocessed config)
  const processConfigurationOnDemand = useCallback(
    (configId: string) => {
      if (!configurations) return;

      const config = configurations.find((c) => c.id === configId);
      if (!config) return;

      // Skip if already has candidates or already processed
      if (config.candidates && config.candidates.length > 0) return;
      if (processedConfigIdsRef.current.has(configId)) return;

      processConfiguration(config, true);
    },
    [configurations, processConfiguration]
  );

  // Auto-process the selected configuration when it changes and needs processing
  // This handles both initial load and when selection changes (e.g., after deletion)
  useEffect(() => {
    if (!projectId || !project || !configurations || !selectedConfigurationId) return;

    // Ensure foundation data exists before processing
    const hasDateRecords = project.dateRecords && project.dateRecords.length > 0;
    const hasTaxMap = project.taxableValueMap && Object.keys(project.taxableValueMap).length > 0;
    if (!hasDateRecords || !hasTaxMap) return;

    // Find the selected configuration
    const selectedConfig = configurations.find((c) => c.id === selectedConfigurationId);
    if (!selectedConfig) return;

    // Skip if already processed in this session
    if (processedConfigIdsRef.current.has(selectedConfigurationId)) return;

    // Only process if it needs processing (has bonds but no candidates)
    if (
      selectedConfig.bondSeries &&
      selectedConfig.bondSeries.length > 0 &&
      (!selectedConfig.candidates || selectedConfig.candidates.length === 0)
    ) {
      processConfiguration(selectedConfig, true);
    }
  }, [projectId, project, configurations, selectedConfigurationId, processConfiguration]);

  return {
    processConfigurationOnDemand,
    configNeedsProcessing,
  };
};
